import type { Product } from '@fuel-system/contracts';

/**
 * Services that gate each home action (fuel-pda.md 2.2 and 4.1). The PDA no
 * longer creates sales (KAN-54): PDA_SALE_CREATE stays on the server only.
 */
export const PDA_SERVICES = {
  purchase: 'PDA_PURCHASE_CREATE',
  delivery: 'PDA_DELIVERY_COMPLETE',
  recent: 'PDA_RECEIPT_REPRINT',
} as const;

export const PRODUCT_ACCESS_SERVICES: Record<Product, string> = {
  DIESEL: 'DIESEL_ACCESS',
  PETROL: 'PETROL_ACCESS',
};

export const PRODUCT_STORAGE_KEY = 'fuel_pda_product_choice';

export type HomeAction = keyof typeof PDA_SERVICES;

export interface HomeVisibility {
  purchase: boolean;
  delivery: boolean;
  recent: boolean;
  products: Product[];
}

/** Which buttons and product tiles a session may show. */
export function homeVisibility(services: readonly string[]): HomeVisibility {
  const held = new Set(services);
  return {
    purchase: held.has(PDA_SERVICES.purchase),
    delivery: held.has(PDA_SERVICES.delivery),
    recent: held.has(PDA_SERVICES.recent),
    products: (['DIESEL', 'PETROL'] as const).filter((p) => held.has(PRODUCT_ACCESS_SERVICES[p])),
  };
}

export function readStoredProduct(): Product | null {
  try {
    const value = globalThis.localStorage?.getItem(PRODUCT_STORAGE_KEY);
    return value === 'DIESEL' || value === 'PETROL' ? value : null;
  } catch {
    return null;
  }
}

export function storeProduct(product: Product): void {
  try {
    globalThis.localStorage?.setItem(PRODUCT_STORAGE_KEY, product);
  } catch {
    // Best effort: the choice is simply not remembered.
  }
}

/** The stored choice when still allowed, otherwise the first allowed product. */
export function initialProduct(allowed: readonly Product[]): Product | null {
  const stored = readStoredProduct();
  if (stored && allowed.includes(stored)) return stored;
  return allowed[0] ?? null;
}
